import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, ComposedChart,
} from 'recharts';
import InvolvementDrillDownModal from './InvolvementDrillDownModal';

const C = ['#0B3D91', '#E31837', '#2196F3', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899', '#4FC3F7'];

function pieEntries(countsObj) {
  return Object.entries(countsObj || {})
    .map(([name, value]) => ({ name: name || 'Unknown', value }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);
}

export default function SessionsSection({ data }) {
  const [view, setView] = useState('overview');
  const [weightByParticipants, setWeightByParticipants] = useState(false);
  const [drillDown, setDrillDown] = useState(null);
  const [showUnmapped, setShowUnmapped] = useState(false);

  const {
    sessions_by_region,
    top_organisations,
    development_officers,
    activity_types,
    venue_types,
    org_types,
    frequency_types,
    delivery_types,
    activity_types_participants,
    venue_types_participants,
    org_types_participants,
    frequency_types_participants,
    delivery_types_participants,
    age_ranges,
    gps_points,
    involvement_total_participants,
    involvement_unallocated_participants,
    involvement_mapped_participants,
    involvement_total_num_sessions,
    involvement_disabled_total,
    involvement_rows_unmapped_region,
    involvement_session_report_count,
    sessions_trend_monthly,
    _sessions: rawSessions,
  } = data;

  const regionData = (sessions_by_region || []).filter(s => s.region !== 'Unknown');
  const unknownRegionRow = (sessions_by_region || []).find(s => s.region === 'Unknown');

  const actData = useMemo(() => {
    const src = weightByParticipants ? activity_types_participants : activity_types;
    return pieEntries(src);
  }, [weightByParticipants, activity_types, activity_types_participants]);

  const venueData = useMemo(() => {
    const src = weightByParticipants ? venue_types_participants : venue_types;
    return pieEntries(src);
  }, [weightByParticipants, venue_types, venue_types_participants]);

  const orgTypeData = useMemo(() => {
    const src = weightByParticipants ? org_types_participants : org_types;
    return pieEntries(src);
  }, [weightByParticipants, org_types, org_types_participants]);

  const freqData = useMemo(() => {
    const src = weightByParticipants ? frequency_types_participants : frequency_types;
    return pieEntries(src);
  }, [weightByParticipants, frequency_types, frequency_types_participants]);

  const deliveryData = useMemo(() => {
    const src = weightByParticipants ? delivery_types_participants : delivery_types;
    return pieEntries(src);
  }, [weightByParticipants, delivery_types, delivery_types_participants]);

  const ageData = (age_ranges || []).map(a => ({
    name: `${a.start}-${a.end} yrs`,
    count: a.count,
    participants: a.participants || 0,
  }));

  const officers = (development_officers || []);
  const orgData = (top_organisations || []).slice(0, 15);
  const totalParticipantsMapped = regionData.reduce((s, r) => s + r.total, 0);
  const gpsCount = (gps_points || []).length;

  // Distinct unmapped locations with participant totals
  const unmappedLocations = useMemo(() => {
    const groups = {};
    (rawSessions || []).filter(s => !s.region || String(s.region).trim() === '').forEach(s => {
      const loc = s.location || '(no location recorded)';
      if (!groups[loc]) groups[loc] = { location: loc, reports: 0, participants: 0 };
      groups[loc].reports += 1;
      groups[loc].participants += (Number(s.total_participants) || 0);
    });
    return Object.values(groups).sort((a, b) => b.reports - a.reports);
  }, [rawSessions]);

  const views = [
    { id: 'overview', label: 'Overview' },
    { id: 'activities', label: 'Activity Types' },
    { id: 'officers', label: 'Development Officers' },
    { id: 'organisations', label: 'Organisations' },
  ];

  const pieValueLabel = weightByParticipants ? 'participants' : 'reports';

  return (
    <div data-testid="sessions-section">
      <div className="section-title">Involvement Sessions</div>
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 1rem', maxWidth: '52rem', lineHeight: 1.45 }}>
        Data source: <strong>Final 2025 Data capture tool export</strong> only.{' '}
        <strong>National participants</strong> sums every row’s TotalParticipants (or male+female+disabled).{' '}
        Rows without a mapped region count as <strong>Unallocated</strong>; regional charts below use only named regions (plus optional Unknown totals).
      </p>

      <div
        className="kpi-grid"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          marginBottom: '1rem',
          gap: '0.75rem',
        }}
      >
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#0B3D91' }}>
            {(involvement_total_participants ?? 0).toLocaleString()}
          </div>
          <div className="kpi-label">National participants</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#F59E0B' }}>
            {(involvement_unallocated_participants ?? 0).toLocaleString()}
          </div>
          <div className="kpi-label">Unallocated</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 4 }}>
            {involvement_rows_unmapped_region ?? 0} reports, no region
          </div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#E31837' }}>
            {(involvement_mapped_participants ?? totalParticipantsMapped).toLocaleString()}
          </div>
          <div className="kpi-label">Mapped to regions</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value">{(involvement_session_report_count ?? 0).toLocaleString()}</div>
          <div className="kpi-label">Session reports</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#8B5CF6' }}>
            {(involvement_total_num_sessions ?? 0).toLocaleString()}
          </div>
          <div className="kpi-label">Delivery instances</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 4 }}>Σ NumberOfSessions</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#10B981' }}>
            {(involvement_disabled_total ?? 0).toLocaleString()}
          </div>
          <div className="kpi-label">Participants (disabled)</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#2196F3' }}>{regionData.length}</div>
          <div className="kpi-label">Regions (chart)</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#10B981' }}>{officers.length}</div>
          <div className="kpi-label">Dev officers</div>
        </div>
        <div className="glass-card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div className="kpi-value" style={{ color: '#F59E0B' }}>{gpsCount}</div>
          <div className="kpi-label">GPS clusters</div>
        </div>
      </div>

      {unmappedLocations.length > 0 && (
        <div className="glass-card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderLeft: '3px solid #F59E0B' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#F59E0B' }}>
              Data Quality — {unmappedLocations.length} location{unmappedLocations.length !== 1 ? 's' : ''} could not be mapped to a region
              ({involvement_rows_unmapped_region ?? 0} session reports, excluded from regional charts)
            </span>
            <button
              type="button"
              onClick={() => setShowUnmapped(v => !v)}
              style={{ fontSize: '0.72rem', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {showUnmapped ? 'Hide' : 'Show details'}
            </button>
          </div>
          {showUnmapped && (
            <table style={{ width: '100%', marginTop: '0.6rem', fontSize: '0.72rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                  <th style={{ padding: '0.2rem 0.5rem 0.2rem 0' }}>Location (raw)</th>
                  <th style={{ padding: '0.2rem 0.5rem', textAlign: 'right' }}>Reports</th>
                  <th style={{ padding: '0.2rem 0 0.2rem 0.5rem', textAlign: 'right' }}>Participants</th>
                </tr>
              </thead>
              <tbody>
                {unmappedLocations.map(r => (
                  <tr key={r.location} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <td style={{ padding: '0.2rem 0.5rem 0.2rem 0', color: 'var(--text-primary)' }}>{r.location}</td>
                    <td style={{ padding: '0.2rem 0.5rem', textAlign: 'right', color: 'var(--text-muted)' }}>{r.reports}</td>
                    <td style={{ padding: '0.2rem 0 0.2rem 0.5rem', textAlign: 'right', color: 'var(--text-muted)' }}>{r.participants.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="people-pills" style={{ marginBottom: '1rem' }}>
        {views.map(v => (
          <button
            key={v.id}
            type="button"
            className={`people-pill ${view === v.id ? 'active' : ''}`}
            onClick={() => setView(v.id)}
            data-testid={`session-filter-${v.id}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === 'overview' && (
        <>
          <div className="charts-grid">
            <div className="glass-card chart-card">
              <h3>Sessions by region (participants)</h3>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>
                Male / female / disabled (stacked). Named regions only.
              </p>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={regionData} margin={{ bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="region" tick={{ fontSize: 10, angle: -40 }} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                  <Legend />
                  <Bar dataKey="male" stackId="a" fill="#0B3D91" name="Male" />
                  <Bar dataKey="female" stackId="a" fill="#E31837" name="Female" />
                  <Bar dataKey="disabled" stackId="a" fill="#10B981" name="Disabled" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="glass-card chart-card">
              <h3>Participant age ranges</h3>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>
                Session reports (left axis) vs participants (right axis).
              </p>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={ageData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="count" name="Reports" fill="#0B3D91" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="participants" name="Participants" fill="#E31837" radius={[4, 4, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
          {(sessions_trend_monthly || []).length > 0 && (
            <div className="glass-card chart-card" style={{ marginTop: '1rem' }}>
              <h3>Trend by month (start date)</h3>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={sessions_trend_monthly} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={55} />
                  <YAxis yAxisId="p" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                  <Legend />
                  <Line yAxisId="p" type="monotone" dataKey="participants" name="Participants" stroke="#E31837" strokeWidth={2} dot={false} />
                  <Line yAxisId="r" type="monotone" dataKey="reports" name="Reports" stroke="#0B3D91" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}

      {view === 'activities' && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Pie charts:</span>
            <button
              type="button"
              className={`people-pill ${!weightByParticipants ? 'active' : ''}`}
              onClick={() => setWeightByParticipants(false)}
            >
              By report count
            </button>
            <button
              type="button"
              className={`people-pill ${weightByParticipants ? 'active' : ''}`}
              onClick={() => setWeightByParticipants(true)}
            >
              By participants
            </button>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              Slices show share of {pieValueLabel}.
            </span>
          </div>
          <div className="charts-grid">
            <div className="glass-card chart-card">
              <h3>Activity types</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={actData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={110}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name.length > 20 ? `${name.slice(0, 20)}…` : name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {actData.map((_, i) => (
                      <Cell key={i} fill={C[i % C.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => v.toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="glass-card chart-card">
              <h3>Venue types</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={venueData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={110}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${String(name).replace('Other ', '').replace(' Facility', '')} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {venueData.map((_, i) => (
                      <Cell key={i} fill={C[i % C.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => v.toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="glass-card chart-card">
              <h3>Organisation types</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orgTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={110}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {orgTypeData.map((_, i) => (
                      <Cell key={i} fill={C[i % C.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => v.toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="glass-card chart-card">
              <h3>Frequency</h3>
              {freqData.length === 0 ? (
                <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No frequency_type values in session data.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={freqData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={110}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${String(name).length > 18 ? `${String(name).slice(0, 18)}…` : name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {freqData.map((_, i) => (
                        <Cell key={i} fill={C[i % C.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => v.toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="glass-card chart-card">
              <h3>Delivery type</h3>
              {deliveryData.length === 0 ? (
                <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No delivery_type values in session data.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={deliveryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={110}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${String(name).length > 18 ? `${String(name).slice(0, 18)}…` : name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {deliveryData.map((_, i) => (
                        <Cell key={i} fill={C[i % C.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => v.toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}

      {view === 'officers' && (
        <div className="glass-card" style={{ padding: '0', overflow: 'auto' }}>
          <div style={{ padding: '1rem 1.25rem 0' }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700 }}>
              Development officers
            </h3>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
              Cricket Namibia development officers delivering sessions across the country.{' '}
              <strong style={{ color: 'var(--text-secondary)' }}>Click a row</strong> for timeline, activity spread, and venue mix.
            </p>
          </div>
          <table className="data-table" data-testid="officers-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th className="num">Session entries</th>
                <th className="num">Participants reached</th>
                <th>Regions</th>
              </tr>
            </thead>
            <tbody>
              {officers.map((o, i) => (
                <tr
                  key={i}
                  className="drill-row-clickable"
                  onClick={() => setDrillDown({ variant: 'officer', officer: o })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDrillDown({ variant: 'officer', officer: o });
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{i + 1}</td>
                  <td style={{ fontWeight: 600 }}>{o.name}</td>
                  <td className="num">{o.session_entries}</td>
                  <td className="num" style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>
                    {(o.total_participants || 0).toLocaleString()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {(o.regions || []).map((r, j) => (
                        <span
                          key={j}
                          style={{
                            padding: '0.1rem 0.4rem',
                            borderRadius: 8,
                            background: 'rgba(11,61,145,0.08)',
                            fontSize: '0.68rem',
                            color: 'var(--brand-primary)',
                            fontWeight: 500,
                          }}
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === 'organisations' && (
        <div className="glass-card" style={{ padding: '0', overflow: 'auto' }}>
          <div style={{ padding: '1rem 1.25rem 0' }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700 }}>
              Top organisations by participant reach
            </h3>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.35rem 0 0' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Click a row</strong> for the same timeline and charts for that organisation (and region when listed).
            </p>
          </div>
          <table className="data-table" data-testid="organisations-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Organisation</th>
                <th>Region</th>
                <th className="num">Sessions</th>
                <th className="num">Participants</th>
              </tr>
            </thead>
            <tbody>
              {orgData.map((o, i) => (
                <tr
                  key={i}
                  className="drill-row-clickable"
                  onClick={() => setDrillDown({ variant: 'organisation', organisation: o })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDrillDown({ variant: 'organisation', organisation: o });
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{o.name}</td>
                  <td>{o.region}</td>
                  <td className="num">{o.sessions}</td>
                  <td className="num" style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>
                    {o.participants.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InvolvementDrillDownModal
        open={drillDown != null}
        onClose={() => setDrillDown(null)}
        variant={drillDown?.variant ?? null}
        officer={drillDown?.officer}
        organisation={drillDown?.organisation}
        sessions={rawSessions || []}
      />
    </div>
  );
}
